import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { CloseIcon } from 'src/components/privilages/pop-up-window/PopUp';
import { addButtonColor, mainAccordingColor, textColor } from 'src/components/sub-components/colors/colors';
import { TextBar } from 'src/components/sub-components/TextBar';
import { UserRoleAccordingList } from './according-list/UserRoleAccordingList';
import { environment } from 'src/environments/environment';
import { ExpandIcon } from 'src/components/privilages/according-list/AccordingList';


const useStyles = makeStyles((themes: Theme) =>
  createStyles({
    inputGridsLeft: {
      margin: '5px',
    },
    text: {
      marginTop: "10px",
      marginBottom: "5px",
      color: textColor
    },
    tableHeader: {
      backgroundColor: "white",
      color: "black",
      marginTop: "15px",
      paddingTop: "10px",
      paddingBottom: "10px"
    },
    add: {
      backgroundColor: addButtonColor,
      color: "white",
      marginBottom: "10px",
      marginRight: "10px",
      width: "20%",
      boxShadow: "none",
      "&:hover": {
        boxShadow: "none",
      }
    },
    cancel: {
      backgroundColor: "white",
      color: "#707070",
      marginBottom: "10px",
      marginRight: "10px",
      width: "20%",
      boxShadow: "none",
      "&:hover": {
        boxShadow: "none",
      }
    },
    highPrivilegeAccording: {
      backgroundColor: mainAccordingColor,
      width: "100%",
      marginBottom: "1px",
    },
  })
)

export function UserRolePopUp(props) {
  const classes = useStyles();

  const [allPrivileges, setAllPrivileges] = useState([]);
  const [show, setShow] = useState(-1);
  const [loading, setLoading] = useState(false);

  const openEdit = (i) => {
    if (i === show) {
      setShow(-1)
    } else {
      setShow(i)
    }
  };

  // Helper function to recursively mark selected status
  const markSelectedStatus = useCallback((privileges, userPrivileges) => {
    return privileges.map(p => {
      const newPrivilege = { ...p };
      const matchedUserPrivilege = userPrivileges.find(up => up.id === newPrivilege.id);

      if (matchedUserPrivilege) {
        newPrivilege.selected = true;
      } else {
        newPrivilege.selected = false;
      }

      if (newPrivilege.actions && newPrivilege.actions.length > 0) {
        newPrivilege.actions = newPrivilege.actions.map(action => {
          const newAction = { ...action };
          if (matchedUserPrivilege && matchedUserPrivilege.actions && matchedUserPrivilege.actions.some(ua => ua.action_name === newAction.action_name)) {
            newAction.selected = true;
          } else {
            newAction.selected = false;
          }
          return newAction;
        });

        const allActionsSelected = newPrivilege.actions.every(a => a.selected);
        const someActionsSelected = newPrivilege.actions.some(a => a.selected);

        if (allActionsSelected) {
          newPrivilege.selected = true;
        } else if (someActionsSelected) {
          newPrivilege.selected = 'partial';
        } else if (newPrivilege.selected === true && !allActionsSelected) {
          newPrivilege.selected = 'partial';
        }
      }

      if (newPrivilege.child && newPrivilege.child.length > 0) {
        newPrivilege.child = markSelectedStatus(newPrivilege.child, userPrivileges);

        const allChildrenSelected = newPrivilege.child.every(c => c.selected === true);
        const someChildrenSelected = newPrivilege.child.some(c => c.selected === true || c.selected === 'partial');

        if (allChildrenSelected && newPrivilege.selected !== 'partial') {
          newPrivilege.selected = true;
        } else if (someChildrenSelected) {
          newPrivilege.selected = 'partial';
        } else if (newPrivilege.selected === true && !allChildrenSelected) {
          newPrivilege.selected = 'partial';
        }
      }

      return newPrivilege;
    });
  }, []);

  const getPrivileges = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${environment.host}/ums/super/user-privileges`, {
        method: "GET",
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('ACCESS_TOKEN') }
      });
      const data = await response.json();

      if (data.message === "Success") {
        const initializedPrivileges = data.content.map(p => {
          const newP = { ...p, selected: false };
          if (newP.actions) {
            newP.actions = newP.actions.map(a => ({ ...a, selected: false }));
          }
          if (newP.child) {
            const initializeChildren = (children) => {
              return children.map(c => {
                const newC = { ...c, selected: false };
                if (newC.actions) {
                  newC.actions = newC.actions.map(a => ({ ...a, selected: false }));
                }
                if (newC.child) {
                  newC.child = initializeChildren(newC.child);
                }
                return newC;
              });
            };
            newP.child = initializeChildren(newP.child);
          }
          return newP;
        });
        setAllPrivileges(initializedPrivileges);

        if (props.type === 'view') {
          await getCurrentPrivilege(initializedPrivileges);
        } else {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching privileges:", error);
      setLoading(false);
    }
  }, [props.type]);

  const getCurrentPrivilege = useCallback(async (allFetchedPrivileges) => {
    setLoading(true);
    try {
      const response = await fetch(`${environment.host}/ums/super/user-role/${props.userRoleId}`, {
        method: "GET",
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('ACCESS_TOKEN') }
      });
      const data = await response.json();

      if (data.message === "Success") {
        const userPrivileges = data.content.userPrivileges || [];
        const updatedPrivileges = markSelectedStatus(allFetchedPrivileges, userPrivileges);
        setAllPrivileges(updatedPrivileges);
        setLoading(false);
      } else {

        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching current privileges:", error);
      setLoading(false);

    }
  }, [props.userRoleId, markSelectedStatus]);

  useEffect(() => {
    if (props.open) {
      getPrivileges();
    }
  }, [props.open, getPrivileges]);

  return (
    <Dialog open={props.open} maxWidth="md">
      <Grid container>
        <Grid item xs={10} md={10}>
          <DialogTitle>View User Privilege Details</DialogTitle>
        </Grid>
        <Grid item xs={2} md={2} style={{ textAlign: "right" }}>
          <IconButton onClick={() => {
            props.onClose()
          }}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>

      <Divider />
      <DialogContent>
        <Grid container>
          <Grid item xs={12} md={5} className={classes.inputGridsLeft}>
            <Typography className={classes.text}>Role ID</Typography>
            <TextBar defaultValue={props.userRoleId} width="100%" type="text" error={{ error: false, errorMsg: "" }} />
          </Grid>
          <Grid item xs={12} md={5} className={classes.inputGridsLeft}>
            <Typography className={classes.text}>Role Name</Typography>
            <TextBar defaultValue={props.userName} width="100%" type="text" error={{ error: false, errorMsg: "" }} />
          </Grid>
          <Grid item xs={12} md={12} className={classes.inputGridsLeft}>
            <Typography className={classes.text}>Role Description</Typography>
            <TextBar defaultValue={props.description} width="100%" type="text" error={{ error: false, errorMsg: "" }} />
          </Grid>

          <Grid container className={classes.tableHeader}>
            <Grid item xs={6} md={6} style={{ textAlign: "center" }}>
              <Typography style={{ fontWeight: "bold" }}>PRIVILEGES</Typography>
            </Grid>
            <Grid item xs={6} md={6} style={{ textAlign: "center" }}>
              <Typography style={{ fontWeight: "bold" }}>ACTIONS</Typography>
            </Grid>
          </Grid>

          <Grid container direction="column" spacing={1}>
            {loading ? (
              <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', height: '200px', alignItems: 'center' }}>
                <CircularProgress size={60} />
              </Grid>
            ) : allPrivileges.length === 0 ? (
              <Grid item xs={12}>
                <Typography align="center" style={{ marginBottom: 20 }}>No privileges available</Typography>
              </Grid>
            ) : (
              <Grid container direction="column" style={{ marginBottom: '20px' }}>
                {allPrivileges.map((privilege, index) => (
                  <CustomAccordion
                    key={index}
                    elevation={0}
                    TransitionProps={{ unmountOnExit: true }}
                    className={classes.highPrivilegeAccording}
                    expanded={index === show}
                  >
                    <AccordionSummary
                      expandIcon={privilege.child && privilege.child.length > 0 ? <ExpandIcon /> : null}
                      onClick={() => openEdit(index)}
                      style={{
                        paddingRight: privilege.child && privilege.child.length === 0 ? '30px' : '0px'
                      }}
                    >
                      <Grid container alignItems="center">
                        <Grid item xs={1} md={1} style={{ display: 'flex', alignItems: 'center' }}>
                          <Checkbox
                            style={{ color: "rgba(24, 29, 51,0.63)" }}
                            checked={privilege.selected === true}
                            indeterminate={privilege.selected === 'partial'}
                            disabled
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Grid>
                        <Grid item xs={3} md={3} style={{ display: 'flex', alignItems: 'center' }}>
                          <Tooltip title={privilege.name} placement="bottom-start" arrow>
                            <Typography noWrap>
                              {privilege.name.length > 15 ? privilege.name.substring(0, 15) + "..." : privilege.name}
                            </Typography>
                          </Tooltip>
                        </Grid>
                        <Grid
                          item
                          xs={8}
                          md={8}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '10px',
                            paddingLeft: '10px'
                          }}
                        >
                          {privilege.actions && privilege.actions.map((action, actionIndex) => (
                            <div
                              key={actionIndex}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginRight: '10px'
                              }}
                            >
                              <Checkbox
                                style={{ color: "rgba(24, 29, 51,0.63)" }}
                                checked={action.selected || false}
                                disabled
                                onClick={(e) => e.stopPropagation()}
                              />
                              <Typography variant="body2">{action.action_name}</Typography>
                            </div>
                          ))}
                        </Grid>
                      </Grid>
                    </AccordionSummary>
                    <AccordionDetails style={{ backgroundColor: "white", padding: 0 }}>
                      <UserRoleAccordingList data={privilege.child} opacity={0.25} padding={1} />
                    </AccordionDetails>
                  </CustomAccordion>
                ))}
              </Grid>

            )}
          </Grid>

        </Grid>
        {loading &&
          <div style={{
            position: 'fixed',
            left: 0,
            top: 0,
            backgroundColor: '#000000',
            opacity: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            width: '100vw',
            height: '100vh'
          }}>
            <CircularProgress size={80} />
          </div>
        }
      </DialogContent>
    </Dialog>
  );
}

const CustomAccordion = withStyles({
  root: {
    "&$expanded": {
      margin: "auto"
    }
  },
  expanded: {}
})(Accordion);

export const CheckBoxIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px"
      fill="rgba(24, 29, 51, 0.64)">
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path
        d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM17.99 9l-1.41-1.42-6.59 6.59-2.58-2.57-1.42 1.41 4 3.99z" />
    </svg>
  )
}
